import type { Infer } from 'superstruct';
import {
	array,
	dynamic,
	nullable,
	number,
	object,
	omit,
	optional,
	partial,
	size,
	string,
	union,
} from 'superstruct';

import { ViewMediaDto } from '@adflow/shared/data-media';
import {
	getFieldParentObject,
	minimumValidatedArray,
	parsableInteger,
	requireFields,
	stringDate,
	stringMaxLength,
	userString,
	userStringWithMaxLength,
	validateCarouselCardImages,
	validateCarouselCardsAspectRatios,
	withMessage,
} from '@adflow/shared/util-superstruct';

// Minimum valid constants for Google Ads validation
export const HEADLINE_MIN_VALID = 3;
export const DISPLAY_AD_HEADLINE_MIN_VALID = 1;
export const DESCRIPTION_MIN_VALID = 2;
export const DEMAND_GEN_CAROUSEL_DESCRIPTION_MIN_VALID = 1;
export const DISPLAY_AD_DESCRIPTION_MIN_VALID = 1;
export const LONG_HEADLINE_MIN_VALID = 1;
export const DISPLAY_URL_PATH_MIN_VALID = 0;
export const VIDEOS_MIN_VALID = 1;
export const CARDS_MIN_VALID = 2;
export const IMAGES_MIN_VALID = 1;
export const CTA_MIN_VALID = 1;
export const DISPLAY_AD_IMAGES_MAX_VALID = 15;
export const DEMAND_GEN_IMAGE_AD_IMAGES_MAX_VALID = 20;
export const DEMAND_GEN_PRODUCT_AD_HEADLINE_MIN_VALID = 1;
export const DEMAND_GEN_PRODUCT_AD_DESCRIPTION_MIN_VALID = 1;

export const ViewDisplayAdObjectDto = object({
	headline: array(string()),
	longHeadline: array(string()),
	description: array(string()),
});

export const ViewSearchAdObjectDto = object({
	headline: array(string()),
	description: array(string()),
	displayUrlPath: array(string()),
});

export const ViewDemandGenSingleImageAdObjectDto = object({
	headline: array(string()),
	description: array(string()),
	finalUrl: string(),
	cta: optional(string()),
});

export const ViewDemandGenVideoAdObjectDto = object({
	headline: array(string()),
	longHeadline: array(string()),
	description: array(string()),
	videos: array(string()),
	siteLinks: optional(nullable(size(array(string()), 1, 4))),
	finalUrl: string(),
	cta: optional(string()),
});

export const ViewDemandGenCarouselCardDto = object({
	images: array(ViewMediaDto),
	headline: string(),
	finalUrl: string(),
	cta: optional(string()),
});

export type ViewDemandGenCarouselCardDto = Infer<
	typeof ViewDemandGenCarouselCardDto
>;

export const ViewDemandGenCarouselAdObjectDto = object({
	cards: array(ViewDemandGenCarouselCardDto),
	headline: array(string()),
	description: array(string()),
});

export const ViewDemandGenProductAdObjectDto = object({
	headline: array(string()),
	description: array(string()),
	cta: optional(string()),
});

export const ViewAdObjectDto = dynamic((_value, { branch }) => {
	const parent = getFieldParentObject(branch);
	if (parent.type === 'RESPONSIVE_SEARCH_AD') {
		return ViewSearchAdObjectDto;
	} else if (parent.type === 'RESPONSIVE_DISPLAY_AD') {
		return ViewDisplayAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_IMAGE') {
		return ViewDemandGenSingleImageAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_VIDEO') {
		return ViewDemandGenVideoAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_CAROUSEL') {
		return ViewDemandGenCarouselAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_PRODUCT_AD') {
		return ViewDemandGenProductAdObjectDto;
	}
	return union([
		ViewDemandGenVideoAdObjectDto,
		ViewDemandGenSingleImageAdObjectDto,
		ViewDemandGenCarouselAdObjectDto,
		ViewDemandGenProductAdObjectDto,
		ViewDisplayAdObjectDto,
		ViewSearchAdObjectDto,
	]);
});

export type ViewAdObjectDto = Infer<typeof ViewAdObjectDto>;

export type ViewDisplayAdObjectDto = Infer<typeof ViewDisplayAdObjectDto>;
export type ViewSearchAdObjectDto = Infer<typeof ViewSearchAdObjectDto>;
export type ViewDemandGenSingleImageAdObjectDto = Infer<
	typeof ViewDemandGenSingleImageAdObjectDto
>;
export type ViewDemandGenVideoAdObjectDto = Infer<
	typeof ViewDemandGenVideoAdObjectDto
>;
export type ViewDemandGenCarouselAdObjectDto = Infer<
	typeof ViewDemandGenCarouselAdObjectDto
>;
export type ViewDemandGenProductAdObjectDto = Infer<
	typeof ViewDemandGenProductAdObjectDto
>;

export const ViewGoogleAdsAdTemplateDto = object({
	id: number(),
	name: string(),
	type: string(),
	status: string(),
	finalUrl: string(),
	label: string(),

	images: optional(array(ViewMediaDto)),

	createdAt: stringDate(),
	updatedAt: stringDate(),

	adObject: optional(nullable(ViewAdObjectDto)),
});

export const AddDisplayAdObjectDto = object({
	headline: minimumValidatedArray(
		size(array(stringMaxLength(30)), 1, 5),
		userString('validations:Required'),
		DISPLAY_AD_HEADLINE_MIN_VALID
	),
	longHeadline: minimumValidatedArray(
		size(array(stringMaxLength(90)), 1, 1),
		userString('validations:Required'),
		LONG_HEADLINE_MIN_VALID
	),
	description: minimumValidatedArray(
		size(array(stringMaxLength(90)), 1, 5),
		userString('validations:Required'),
		DISPLAY_AD_DESCRIPTION_MIN_VALID
	),
});

export const AddSearchAdObjectDto = object({
	headline: minimumValidatedArray(
		size(array(stringMaxLength(30)), HEADLINE_MIN_VALID, 15),
		userString('validations:Required'),
		HEADLINE_MIN_VALID
	),
	description: minimumValidatedArray(
		size(array(stringMaxLength(90)), 2, 4),
		userString('validations:Required'),
		DESCRIPTION_MIN_VALID
	),
	displayUrlPath: minimumValidatedArray(
		size(array(stringMaxLength(15)), 0, 2),
		userString('validations:Required'),
		DISPLAY_URL_PATH_MIN_VALID
	),
});

export const AddDemandGenSingleImageAdObjectDto = object({
	headline: minimumValidatedArray(
		size(array(stringMaxLength(40)), 1, 5),
		userString('validations:Required'),
		HEADLINE_MIN_VALID
	),
	description: minimumValidatedArray(
		size(array(stringMaxLength(90)), 1, 5),
		userString('validations:Required'),
		DESCRIPTION_MIN_VALID
	),
	finalUrl: string(),
	cta: optional(string()),
});

export const AddDemandGenVideoAdObjectDto = object({
	headline: minimumValidatedArray(
		size(array(stringMaxLength(40)), 1, 5),
		userString('validations:Required'),
		HEADLINE_MIN_VALID
	),
	longHeadline: minimumValidatedArray(
		size(array(stringMaxLength(90)), 1, 5),
		userString('validations:Required'),
		LONG_HEADLINE_MIN_VALID
	),
	description: minimumValidatedArray(
		size(array(stringMaxLength(90)), 1, 5),
		userString('validations:Required'),
		DESCRIPTION_MIN_VALID
	),
	finalUrl: string(),
	videos: minimumValidatedArray(
		size(array(stringMaxLength(2048)), 1, 5),
		userString('validations:Required'),
		VIDEOS_MIN_VALID
	),
	cta: optional(string()),
});

export const AddDemandGenCarouselCardDto = object({
	images: minimumValidatedArray(
		validateCarouselCardImages(size(array(ViewMediaDto), 1, 3)),
		ViewMediaDto,
		IMAGES_MIN_VALID
	),
	headline: userStringWithMaxLength(40),
	finalUrl: userStringWithMaxLength(2048),
	cta: optional(string()),
});

export const AddDemandGenCarouselAdObjectDto = object({
	cards: validateCarouselCardsAspectRatios(
		minimumValidatedArray(
			size(array(AddDemandGenCarouselCardDto), CARDS_MIN_VALID, 10),
			omit(AddDemandGenCarouselCardDto, ['images', 'finalUrl', 'headline']),
			CARDS_MIN_VALID
		)
	),
	headline: size(array(userStringWithMaxLength(40)), 1, 1),
	description: size(array(userStringWithMaxLength(90)), 1, 1),
});

export const AddDemandGenProductAdObjectDto = object({
	headline: minimumValidatedArray(
		size(
			array(stringMaxLength(40)),
			DEMAND_GEN_PRODUCT_AD_HEADLINE_MIN_VALID,
			1
		),
		userString('validations:Required'),
		DEMAND_GEN_PRODUCT_AD_HEADLINE_MIN_VALID
	),
	description: minimumValidatedArray(
		size(
			array(stringMaxLength(90)),
			DEMAND_GEN_PRODUCT_AD_DESCRIPTION_MIN_VALID,
			1
		),
		userString('validations:Required'),
		DEMAND_GEN_PRODUCT_AD_DESCRIPTION_MIN_VALID
	),
	cta: optional(string()),
});

export type AddDisplayAdObjectDto = Infer<typeof AddDisplayAdObjectDto>;
export type AddSearchAdObjectDto = Infer<typeof AddSearchAdObjectDto>;
export type AddDemandGenSingleImageAdObjectDto = Infer<
	typeof AddDemandGenSingleImageAdObjectDto
>;
export type AddDemandGenVideoAdObjectDto = Infer<
	typeof AddDemandGenVideoAdObjectDto
>;
export type AddDemandGenCarouselAdObjectDto = Infer<
	typeof AddDemandGenCarouselAdObjectDto
>;
export type AddDemandGenProductAdObjectDto = Infer<
	typeof AddDemandGenProductAdObjectDto
>;

export const AddAdObjectDto = dynamic((_value, { branch }) => {
	const parent = getFieldParentObject(branch);
	if (parent.type === 'RESPONSIVE_SEARCH_AD') {
		return AddSearchAdObjectDto;
	} else if (parent.type === 'RESPONSIVE_DISPLAY_AD') {
		return AddDisplayAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_IMAGE') {
		return AddDemandGenSingleImageAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_VIDEO') {
		return AddDemandGenVideoAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_CAROUSEL') {
		return AddDemandGenCarouselAdObjectDto;
	} else if (parent.type === 'DEMAND_GEN_PRODUCT_AD') {
		return AddDemandGenProductAdObjectDto;
	}
	return union([
		AddDemandGenSingleImageAdObjectDto,
		AddDemandGenVideoAdObjectDto,
		AddDemandGenCarouselAdObjectDto,
		AddDemandGenProductAdObjectDto,
		AddDisplayAdObjectDto,
		AddSearchAdObjectDto,
	]);
});

export type AddAdObjectDto = Infer<typeof AddAdObjectDto>;

export const AddGoogleAdsAdTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	type: requireFields(userString('validations:Required'), ['images'], {
		amountRequired: 1,
		validator: (value) =>
			value === 'RESPONSIVE_DISPLAY_AD' || value === 'DEMAND_GEN_IMAGE',
		message: 'validations:Required media',
	}),
	status: userString('validations:Required'),
	finalUrl: userString('validations:Required'),
	label: optional(string()),

	images: dynamic((_value, { branch }) => {
		const parent = getFieldParentObject(branch);
		if (parent.type === 'RESPONSIVE_DISPLAY_AD') {
			return withMessage(
				size(array(ViewMediaDto), 0, DISPLAY_AD_IMAGES_MAX_VALID),
				'validations:Max Media'
			);
		} else if (parent.type === 'DEMAND_GEN_IMAGE') {
			return withMessage(
				size(array(ViewMediaDto), 0, DEMAND_GEN_IMAGE_AD_IMAGES_MAX_VALID),
				'validations:Max Media'
			);
		}
		return array(ViewMediaDto);
	}),

	adObject: optional(AddAdObjectDto),
});

export const EditGoogleAdsAdTemplateDto = partial(AddGoogleAdsAdTemplateDto);

export type ViewGoogleAdsAdTemplateDto = Infer<
	typeof ViewGoogleAdsAdTemplateDto
>;
export type AddGoogleAdsAdTemplateDto = Infer<typeof AddGoogleAdsAdTemplateDto>;
export type EditGoogleAdsAdTemplateDto = Infer<
	typeof EditGoogleAdsAdTemplateDto
>;
